import React from 'react';
import { motion } from 'framer-motion';
import { Lock, AlertTriangle, Target, EyeOff, Building2, Globe, Zap } from 'lucide-react';

/**
 * "O Que o Mercado Não Vê" — institutional differentiation section.
 * Positions Coutin as an operation trusted with confidential, high-stakes work.
 * No fabricated cases or client names. Language remains professional and discreet.
 */
const MarketDoesntSee = () => {
  const cards = [
    {
      icon: Lock,
      title: 'Produções Confidenciais',
      desc: 'Projetos realizados sob restrições de divulgação, acordos de confidencialidade e necessidades específicas de privacidade.',
    },
    {
      icon: AlertTriangle,
      title: 'Comunicação de Crise',
      desc: 'Produção e execução de conteúdo em momentos que exigem agilidade, controle e responsabilidade.',
    },
    {
      icon: Target,
      title: 'Campanhas Estratégicas',
      desc: 'Conteúdo pensado para objetivos específicos de comunicação, posicionamento, aquisição ou resposta.',
    },
    {
      icon: EyeOff,
      title: 'White-Label',
      desc: 'Execução audiovisual e digital para agências, empresas e parceiros que precisam ampliar sua capacidade sem expor o fornecedor.',
    },
    {
      icon: Building2,
      title: 'Conteúdo Corporativo Sensível',
      desc: 'Produções em que contexto, imagem, informação e confidencialidade precisam ser tratados com cuidado.',
    },
    {
      icon: Globe,
      title: 'Operações na Tríplice Fronteira',
      desc: 'Estrutura e experiência para projetos em Foz do Iguaçu, Ciudad del Este, Puerto Iguazú e região.',
    },
    {
      icon: Zap,
      title: 'Produção com Prazo Crítico',
      desc: 'Capacidade de mobilização e execução para projetos que precisam acontecer dentro de janelas de tempo reduzidas.',
    },
  ];

  return (
    <section
      className="relative py-24 md:py-32 bg-[#050505] border-t border-white/5 overflow-hidden"
      data-testid="market-doesnt-see-section"
    >
      {/* Cinematic backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(78,205,196,0.10)_0%,rgba(0,0,0,0)_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(78,205,196,0.05)_0%,rgba(0,0,0,0)_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(78,205,196,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(78,205,196,0.6) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="inline-flex items-center space-x-2 mb-4">
            <div className="w-8 h-[1px] bg-[#4ECDC4]" />
            <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#4ECDC4]">
              Nos Bastidores
            </span>
            <div className="w-8 h-[1px] bg-[#4ECDC4]" />
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl tracking-tight font-bold text-[#F5F5F0] mb-6 leading-[1.05]">
            O que o mercado <span className="italic font-light text-[#4ECDC4]">não vê</span>.
          </h2>
          <p className="text-base md:text-lg text-[#A1A1AA] max-w-2xl mx-auto font-light leading-relaxed">
            Parte dos trabalhos mais importantes da Coutin acontece nos bastidores — onde
            confidencialidade, velocidade e precisão são tão importantes quanto a qualidade da entrega.
          </p>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.4) }}
                className="group relative border border-white/8 hover:border-[#4ECDC4]/40 rounded-2xl p-6 md:p-7 bg-gradient-to-br from-[#0E0E0E] to-[#050505] hover:from-[#131313] hover:to-[#050505] transition-all duration-400"
                data-testid={`market-card-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#4ECDC4]/12 border border-[#4ECDC4]/30 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-[#4ECDC4]/20 transition-all duration-300">
                  <Icon className="w-5 h-5 text-[#4ECDC4]" strokeWidth={1.8} />
                </div>
                <h3 className="text-base md:text-lg font-bold text-[#F5F5F0] mb-2 leading-tight">
                  {c.title}
                </h3>
                <p className="text-sm text-[#A1A1AA] font-light leading-relaxed">
                  {c.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Institutional statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-16 max-w-2xl mx-auto"
        >
          <p className="text-base md:text-lg text-[#D4D4D8] font-light italic leading-relaxed">
            "Nem todo trabalho pode entrar no portfólio. Alguns exigem discrição,
            confidencialidade e confiança."
          </p>
          <div className="mt-4 w-24 h-px bg-gradient-to-r from-transparent via-[#4ECDC4]/50 to-transparent mx-auto" />
        </motion.div>
      </div>
    </section>
  );
};

export default MarketDoesntSee;
